import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import YouTube from '@/components/YouTube'
import { findChangelog } from '@/models/changelog.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const changelog = findChangelog(params.slug as string)

  if (!changelog) {
    throw new Response('Not Found', { status: 404 })
  }

  const { root } = pageHelper(url)

  return {
    changelog: changelog,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const {
    changelog: { meta },
    root
  } = loaderData

  const title = `${meta.title} — PlanetScale`
  const image = `${root}/og/changelog/${loaderData.changelog.slug}.png`

  return [
    { title },
    { name: 'description', content: meta.description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: meta.canonical },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: meta.description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: meta.canonical },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: meta.description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: meta.canonical }
  ]
}

export default function Changelog() {
  const { changelog } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl flex-1 px-3 sm:px-5 lg:px-12'>
        <article className='min-w-0'>
          <h1>{changelog.title}</h1>

          <p className='text-secondary'>
            <time dateTime={changelog.createdAt}>{formatLong(changelog.createdAt)}</time>
          </p>

          {renderers.react(changelog.body, React, {
            components: {
              ContentImage,
              YouTube
            }
          })}
        </article>
      </main>

      <SiteFooter />
    </>
  )
}
